import React, { useState } from 'react';
import { Contract, Milestone } from '../../types/contract';
import { formatCurrency, formatDateTime } from '../../utils/contractHelpers';
import { DollarSign, Lock, CheckCircle, Clock, AlertTriangle, RefreshCcw, CreditCard } from 'lucide-react';
import { Button } from '../UI/Button';
import PaymentModal from './PaymentModal';

interface PaymentEscrowPanelProps {
  contract: Contract;
  milestones: Milestone[];
  currentUserId?: string;
  onPaid?: () => void;
}

const STATUS_LABEL: Record<string, { label: string; tone: 'amber' | 'emerald' | 'blue' | 'slate' | 'red' }> = {
  NOT_FUNDED: { label: '待支付', tone: 'amber' },
  PAYMENT_PENDING: { label: '支付处理中', tone: 'blue' },
  FULLY_FUNDED: { label: '已托管', tone: 'emerald' },
  EXPIRED: { label: '支付已超时', tone: 'red' },
  FULLY_RELEASED: { label: '已全部释放', tone: 'emerald' },
  PARTIALLY_REFUNDED: { label: '部分退款', tone: 'slate' },
};

const TONE_CLASS: Record<string, string> = {
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  slate: 'bg-slate-50 text-slate-700 border-slate-200',
  red: 'bg-red-50 text-red-700 border-red-200',
};

export const PaymentEscrowPanel: React.FC<PaymentEscrowPanelProps> = ({
  contract,
  milestones,
  currentUserId,
  onPaid,
}) => {
  const [showPayModal, setShowPayModal] = useState(false);

  const paymentStatus = (contract.payment_status as keyof typeof STATUS_LABEL) || 'NOT_FUNDED';
  const isClient = currentUserId === contract.client_user_id;
  const canPay = isClient
    && contract.status === 'ACTIVE'
    && (paymentStatus === 'NOT_FUNDED' || paymentStatus === 'PAYMENT_PENDING' || paymentStatus === 'EXPIRED');

  const totalReleased = milestones
    .filter(m => m.payment_status === 'RELEASED')
    .reduce((s, m) => s + (m.released_amount || 0), 0);
  const totalFee = milestones
    .filter(m => m.payment_status === 'RELEASED')
    .reduce((s, m) => s + (m.platform_fee_amount || 0), 0);
  const totalRefunded = milestones
    .filter(m => m.payment_status === 'REFUNDED')
    .reduce((s, m) => s + (m.payment_amount || 0), 0);
  const totalEscrowed = milestones
    .filter(m => m.payment_status === 'FUNDED')
    .reduce((s, m) => s + (m.payment_amount || 0), 0);

  const releasedPct = contract.total_amount > 0
    ? Math.min(100, ((totalReleased + totalFee) / contract.total_amount) * 100)
    : 0;

  const tone = STATUS_LABEL[paymentStatus] || STATUS_LABEL.NOT_FUNDED;

  return (
    <>
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-slate-600" />
            <h3 className="text-lg font-semibold text-slate-900">托管支付</h3>
          </div>
          <span className={`text-xs px-2 py-1 rounded border ${TONE_CLASS[tone.tone]}`}>
            {tone.label}
          </span>
        </div>

        {/* Total */}
        <div className="mb-5">
          <p className="text-sm text-slate-600 mb-1">合同总金额</p>
          <p className="text-3xl font-bold text-slate-900">
            {formatCurrency(contract.total_amount, contract.currency)}
          </p>
          {contract.platform_fee_rate ? (
            <p className="text-xs text-slate-500 mt-1">
              平台服务费 {(contract.platform_fee_rate * 100).toFixed(0)}%（每期释放时由乙方承担）
            </p>
          ) : null}
        </div>

        {/* Pay button */}
        {canPay && (
          <div className="mb-5">
            <Button onClick={() => setShowPayModal(true)} className="w-full inline-flex items-center justify-center gap-2">
              <CreditCard className="w-4 h-4" />
              {paymentStatus === 'EXPIRED' ? '重新发起支付' : paymentStatus === 'PAYMENT_PENDING' ? '继续支付' : '立即支付'}
            </Button>
            {paymentStatus === 'NOT_FUNDED' && (
              <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                请在签署后 24 小时内完成支付，逾期合同自动取消
              </p>
            )}
          </div>
        )}

        {/* Progress bar (only meaningful after funding) */}
        {(paymentStatus === 'FULLY_FUNDED' || paymentStatus === 'FULLY_RELEASED' || paymentStatus === 'PARTIALLY_REFUNDED') && (
          <div className="mb-5">
            <div className="flex items-center justify-between text-xs text-slate-600 mb-2">
              <span>已释放</span>
              <span className="font-semibold">{releasedPct.toFixed(0)}%</span>
            </div>
            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${releasedPct}%` }} />
            </div>
          </div>
        )}

        {/* Breakdown */}
        <div className="space-y-2 mb-5">
          <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              <span className="text-sm text-slate-700">已释放给乙方</span>
            </div>
            <span className="text-sm font-semibold text-emerald-700">
              {formatCurrency(totalReleased, contract.currency)}
            </span>
          </div>
          {totalFee > 0 && (
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-slate-600" />
                <span className="text-sm text-slate-700">平台服务费</span>
              </div>
              <span className="text-sm font-semibold text-slate-700">
                {formatCurrency(totalFee, contract.currency)}
              </span>
            </div>
          )}
          {totalEscrowed > 0 && (
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-slate-700">托管中</span>
              </div>
              <span className="text-sm font-semibold text-blue-700">
                {formatCurrency(totalEscrowed, contract.currency)}
              </span>
            </div>
          )}
          {totalRefunded > 0 && (
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
              <div className="flex items-center gap-2">
                <RefreshCcw className="w-4 h-4 text-amber-600" />
                <span className="text-sm text-slate-700">已退款给甲方</span>
              </div>
              <span className="text-sm font-semibold text-amber-700">
                {formatCurrency(totalRefunded, contract.currency)}
              </span>
            </div>
          )}
        </div>

        {/* Released history */}
        {milestones.some(m => m.payment_status === 'RELEASED') && (
          <div className="pt-5 border-t border-slate-200">
            <h4 className="text-sm font-semibold text-slate-900 mb-3">释放记录</h4>
            <div className="space-y-2">
              {milestones
                .filter(m => m.payment_status === 'RELEASED')
                .sort((a, b) => (b.released_at || 0) - (a.released_at || 0))
                .slice(0, 5)
                .map(m => (
                  <div key={m.milestone_id} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-900 truncate">{m.title}</p>
                      <p className="text-xs text-slate-500">{m.released_at ? formatDateTime(Number(m.released_at)) : ''}</p>
                    </div>
                    <span className="text-xs font-semibold text-emerald-700 ml-2">
                      +{formatCurrency(m.released_amount || 0, contract.currency)}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Info banner */}
        {paymentStatus === 'FULLY_FUNDED' && (
          <div className="mt-5 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Lock className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-800">
                合同款项已托管在支付宝直付通账户，里程碑通过审核后自动释放给创作者。
              </p>
            </div>
          </div>
        )}
        {paymentStatus === 'EXPIRED' && (
          <div className="mt-5 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Clock className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-800">
                支付订单已超过 24 小时支付窗口。可点击上方按钮重新发起支付。
              </p>
            </div>
          </div>
        )}
      </div>

      <PaymentModal
        isOpen={showPayModal}
        contractId={contract.contract_id}
        contractTitle={contract.title}
        amount={contract.total_amount}
        currency={contract.currency}
        onClose={() => setShowPayModal(false)}
        onPaid={() => { setShowPayModal(false); onPaid?.(); }}
      />
    </>
  );
};
